import { Utils } from '../../../../../utils/common'
import { DOMEvents, DOMUtils } from '../../../../../utils/dom'
import { Store } from '../../../../../utils/storage/keys'
import { LocalStore } from '../../../../../utils/storage/local'
import { AnalyticsService } from '../../../../analytics'
import { IdentityEvent } from '../../../../common/events/identity'
import { IProfile } from '../../../../common/profile'
import SailthruAdapter from '../../sailthru'
import { LoginRadiusAdapter } from '../index'
import { ISuccessResponse } from '../widgets/login-widget'

export interface IWindow extends Window {
    Sailthru: any
    tp: any
}

declare let window: IWindow

export class LoginService {

    public static async handleLoginSuccess(response: ISuccessResponse) {
        window.tp = window.tp || []

        const token = response.access_token
        const jwtToken = response.jwttoken
        // store token
        localStorage.setItem(Store.JWT, jwtToken)

        // LR returns Data or Profile in the login success response object.
        const data = response.Data || response.Profile

        window.tp.push(['init', () => {
            // Setting the new JWT token for authenticated user
            window.tp.push(['setExternalJWT', jwtToken])
        }])

        const profileData = LoginService.createProfile(data)

        // Get stored redirect URL from where user navigated to the sign in page
        let redirectUrl = sessionStorage ? sessionStorage.getItem(Store.REDIRECT_URL) : undefined

        if (redirectUrl) {
            // remove item from session storage
            sessionStorage.removeItem(Store.REDIRECT_URL)
        } else {
            redirectUrl = location.origin
        }
        document.dispatchEvent(Utils.event(IdentityEvent.IDENTITY_CHANGE, {...profileData, isLogin: true}))

        document.dispatchEvent(new CustomEvent(IdentityEvent.IDENTITY_LOGIN, {
            detail: {
                email: profileData.email,
                uuid: profileData.uuid
            }
        }))
        // add an event listener for profile updated
        document.addEventListener(IdentityEvent.IDENTITY_PROFILE_UPDATED, () => {
            const fromActivateButton = sessionStorage ? sessionStorage.getItem(Store.ACTIVATE_BUTTON) : undefined

            if (fromActivateButton) {
                sessionStorage.removeItem(Store.ACTIVATE_BUTTON)
                const profile = LocalStore.get<IProfile>(Store.ID)
                const printSubscriber = profile.printSubscriber

                if (printSubscriber) {
                    // set redirect to already activated page
                    redirectUrl = `${location.origin}/already-activated`
                }
            }

            // set sailthru cookie
            SailthruAdapter.setCookie(profileData.email, redirectUrl, token)
            // send analytic event
            if (response.Profile.FirstLogin) {
                LoginService.sendRegistrationAnalytics(response.Profile.RegistrationProvider || 'traditionalRegister')
            } else {
                LoginService.sendLoginAnalytics(response.Profile.RegistrationProvider || 'traditionalSignin')
            }
        })

    }

    /**
     * Clear error messages
     */
    public static clearErrors() {
        const errorMsg = document.querySelector(`.${LoginRadiusAdapter.DOM.class.errorMessageContainer}`)
        if (errorMsg) {
            errorMsg.remove()
        }

        // clear input errors
        const inputErrorWrappers = document.querySelectorAll(`.${LoginRadiusAdapter.DOM.class.inputWrapperError}`)

        inputErrorWrappers.forEach((inputErrorWrapper) => {
            const inputField = inputErrorWrapper.querySelector('input')
            const inputError = inputErrorWrapper.querySelector(`.${LoginRadiusAdapter.DOM.class.inputErrorMessage}`)

            DOMUtils.removeClass(inputErrorWrapper, `${LoginRadiusAdapter.DOM.class.inputWrapperError}`)

            if (inputField) {
                inputField.removeAttribute('aria-describedby')
            }

            if (inputError) {
                inputError.remove()
            }
        })
    }

    /**
     * Display any errors that appear after submitting the form
     * @param errors any
     */
    public static showErrors(errors: any) {
        if (Array.isArray(errors)) {
            const isApiErrorArray = !!(errors.length && errors[0].ErrorCode)

            if (isApiErrorArray) {
                const errorDiv = document.createElement('div')
                const errorList = document.createElement('ul')
                DOMUtils.addClass(errorList, 'errorlist')

                // Show api errors
                const identityScreenForm = document.querySelector(`.${LoginRadiusAdapter.DOM.class.identityForm}`)
                errorDiv.setAttribute('role', 'alert')
                errorDiv.className = LoginRadiusAdapter.DOM.class.errorMessageContainer

                const error = errors[0]
                if (error.Description) {
                    const errorListItem = document.createElement('li')
                    errorListItem.innerHTML = error.Description
                    errorList.appendChild(errorListItem)
                }

                if (identityScreenForm) {
                    errorDiv.appendChild(errorList)
                    identityScreenForm.appendChild(errorDiv)
                }
            } else {
                // Show input errors
                errors.map((error) => {
                    if (error.Id && error.Message) {
                        const inputField = document.querySelector(`#${error.Id}`)
                        const inputErrorSpan = document.createElement('span')
                        const inputErrorList = document.createElement('ul')
                        const errorListItem = document.createElement('li')
                        const errorMessageId = `${error.Id}__error`

                        inputField.setAttribute('aria-describedBy', errorMessageId)
                        inputErrorSpan.id = errorMessageId
                        errorListItem.innerHTML = error.Message

                        // Error field styling classes
                        DOMUtils.addClass(inputErrorSpan, LoginRadiusAdapter.DOM.class.inputErrorMessage)
                        DOMUtils.addClass(inputErrorList, 'errorlist')

                        // Add validation error
                        const inputWrapper = inputField.parentElement
                        DOMUtils.addClass(inputWrapper, LoginRadiusAdapter.DOM.class.inputWrapperError)
                        inputErrorList.appendChild(errorListItem)
                        inputErrorSpan.appendChild(inputErrorList)
                        inputWrapper.appendChild(inputErrorSpan)
                    }
                })

                // tslint:disable-next-line: max-line-length
                const firstInputError = document.querySelector(`.${LoginRadiusAdapter.DOM.class.inputWrapperError} input`) as HTMLInputElement

                if (firstInputError) {
                    firstInputError.focus()
                }
            }
        }
    }

    /**
     * Set the back to site link to the redirect url in session storage
     * Redirects to hosted solution if there is a token
     *
     * @param {string} token
     */
    public static setRedirectLink(token?: string) {
        const backToSiteButton = document.querySelector('.button__back-to-site') as HTMLAnchorElement
        let redirectUrl

        if (sessionStorage) {
            if (token) {
                const callbackUrl = (sessionStorage.getItem('redirect_url')) ?
                    sessionStorage.getItem('redirect_url') : location.origin
                redirectUrl = `${LoginRadiusAdapter.ssoRedirectLink}&token=${token}&callback=${callbackUrl}`
            } else {
                redirectUrl = sessionStorage.getItem('redirect_url')
            }
        }

        if (backToSiteButton) {
            if (redirectUrl) {
                backToSiteButton.href = redirectUrl
                sessionStorage.removeItem(redirectUrl)
            }
        }
    }

    /**
     * Send login analytics
     * @param {string} signInMethod: 'social' or 'traditionalSignin'
     */
    public static sendLoginAnalytics(signInMethod: string) {
        document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
            detail: {
                control_fields: {
                    mparticle: {
                        keys: {
                            integration_name: 'integration_name',
                            sign_in_method: 'sign_in_method'
                        },
                        mp_event_type: 'UserContent'
                    }
                },
                event_name: AnalyticsService.customEventNames.signin,
                integration_name: 'LoginRadius',
                sign_in_method: signInMethod
            }
        }))
    }
    /**
     * Send registration analytics event
     * @param {string} registrationMethod: 'social' or 'traditionalSignin'
     */
    public static sendRegistrationAnalytics(registrationMethod: string) {
        document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
            detail: {
                control_fields: {
                    mparticle: {
                        keys: {
                            integration_name: 'integration_name',
                            registration_method: 'registration_method'
                        },
                        mp_event_type: 'UserContent'
                    }
                },
                event_name: AnalyticsService.customEventNames.registration,
                integration_name: 'LoginRadius',
                registration_method: registrationMethod
            }
        }))
    }
    /**
     * Send signout analytics
     */
    public static sendLogoutAnalytics() {
        document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
            detail: {
                control_fields: {
                    mparticle: {
                        keys: {
                            integration_name: 'integration_name'
                        },
                        mp_event_type: 'UserContent'
                    }
                },
                event_name: AnalyticsService.customEventNames.signout,
                integration_name: 'LoginRadius',
            }
        }))
    }

    /**
     * Send submission error analytics on form submission
     */
    public static sendSubmissionErrorAnalytics(errors: any, formName: string) {
        if (Array.isArray(errors) && errors.length) {
            // API error
            if (errors[0].ErrorCode) {
                document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                    detail: {
                        control_fields: {
                            mparticle: {
                                keys: {
                                    error_code: 'error_code',
                                    error_message: 'error_message',
                                    form_name: 'form_name',
                                },
                                mp_event_type: 'UserContent'
                            }
                        },
                        error_code: `${errors[0].ErrorCode}`,
                        error_message: errors[0].Description,
                        event_name: AnalyticsService.customEventNames.submissionError,
                        form_name: formName,
                    }
                }))
            } else {
                // input field error
                errors.map((error) => {
                    document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                        detail: {
                            control_fields: {
                                mparticle: {
                                    keys: {
                                        error_message: 'error_message',
                                        form_name: 'form_name',
                                        input_id: 'input_id',
                                    },
                                    mp_event_type: 'UserContent'
                                }
                            },
                            error_message: error.Message,
                            event_name: AnalyticsService.customEventNames.submissionError,
                            form_name: formName,
                            input_id: error.Id,
                        }
                    }))
                })
            }
        }
    }

    /**
     * Shapes the LoginRadius response into a profile object
     */
    public static createProfile(data: any) {
        return {
            created: data.CreatedDate,
            doNotContact: undefined,
            email: (Array.isArray(data.Email)) ? data.Email[0].Value : '',
            emailConfirmed: data.EmailVerified,
            familyName: data.LastName,
            gender: data.Gender,
            givenName: data.FirstName,
            lastSignIn: data.LastLoginDate,
            name: data.FullName,
            uuid: data.Uid
        } as IProfile
    }

    /**
     * Change footer links to redirect to hosted solution
     *
     * @param {string} token
     */
     public static setSSOFooterLinks(token: string) {
        document.querySelectorAll('.identity-footer a').forEach((link: HTMLAnchorElement) => {
            const href = link.href
            if (href.indexOf('http') !== -1) {
                link.href = `${LoginRadiusAdapter.ssoRedirectLink}&token=${token}&callback=${link.href}`
            } else {
                link.href =
                    `${LoginRadiusAdapter.ssoRedirectLink}&token=${token}&callback=${location.origin}${link.href}`
            }
        })
    }
}
